import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function LoginPage() {
  const { t } = useTranslation();
  const { login, completeMfaLogin, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Set once the password step succeeded on an account that has 2FA.
  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  if (token) {
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { mfaChallenge: challenge } = await login(email, password);
      if (challenge) {
        setMfaChallenge(challenge);
        return;
      }
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('auth.invalidCredentials');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleMfaSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await completeMfaLogin(mfaChallenge!, mfaCode);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('auth.mfaInvalidCode');
      setError(msg);
      setMfaCode('');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-full max-w-sm">
        {/* Logo area */}
        <div className="text-center mb-8">
          <img src="/juni-logo.svg" alt="juni.com" className="h-8 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900">Provisioning Center</h1>
          <p className="text-slate-500 text-sm mt-1">
            {mfaChallenge ? t('auth.mfaTitle') : t('auth.signInSubtitle')}
          </p>
        </div>

        {mfaChallenge ? (
          <form
            onSubmit={handleMfaSubmit}
            className="bg-surface rounded-xl shadow-sm border border-slate-200 px-8 py-8 space-y-5"
          >
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('auth.mfaCodeLabel')}
              </label>
              <input
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                required
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-center text-lg tracking-[0.3em] font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
              />
              <p className="text-xs text-slate-500 mt-2">
                {t('auth.mfaBackupHint')}
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white font-medium py-2.5 rounded-lg text-sm transition-colors"
            >
              {loading ? t('auth.verifying') : t('auth.mfaVerify')}
            </button>

            <button
              type="button"
              onClick={() => {
                setMfaChallenge(null);
                setMfaCode('');
                setError('');
              }}
              className="w-full text-center text-sm text-slate-500 hover:text-slate-700"
            >
              {t('common.cancel')}
            </button>
          </form>
        ) : (
        <form
          onSubmit={handleSubmit}
          className="bg-surface rounded-xl shadow-sm border border-slate-200 px-8 py-8 space-y-5"
        >
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              {t('common.email')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="admin@juni.com"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              {t('auth.password')}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white font-medium py-2.5 rounded-lg text-sm transition-colors"
          >
            {loading ? t('auth.signingIn') : t('auth.signIn')}
          </button>

          <p className="text-center text-sm">
            <Link to="/forgot-password" className="text-brand-600 hover:text-brand-800">
              {t('auth.forgotPassword')}
            </Link>
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
